import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Menu, Sparkles, ArrowRight } from 'lucide-react';
import UserMenu from '@/components/UserMenu';
import GlobalNavMenu from '@/components/GlobalNavMenu';
import Footer from '@/components/Footer';
import { NUMBERS_MAP } from '@/data/numbersMap';

// Distinct hue per number so the grid feels alive, not uniform.
const NUMBER_COLORS = {
  1: 'from-amber-500 to-yellow-400',
  2: 'from-sky-500 to-cyan-400',
  3: 'from-orange-500 to-amber-400',
  4: 'from-emerald-500 to-green-400',
  5: 'from-cyan-500 to-teal-400',
  6: 'from-rose-500 to-pink-400',
  7: 'from-purple-500 to-indigo-400',
  8: 'from-yellow-500 to-amber-400',
  9: 'from-violet-500 to-fuchsia-400',
};

export default function NumbersMapPage() {
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);

  const handleRunDiagnosis = (number) => {
    navigate('/diagnostika', { state: { baseNumber: number } });
  };

  return (
    <div className="min-h-screen overflow-hidden relative" data-testid="numbers-map-page">
      {/* Decorative Background Elements */}
      <div className="absolute top-24 left-8 w-40 h-40 bg-[#d4af37]/10 rounded-full blur-3xl animate-pulse-glow" />
      <div className="absolute bottom-24 right-8 w-52 h-52 bg-purple-500/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Top Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setNavOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#d4af37]/30 text-[#d4af37]/70 hover:text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
            data-testid="nav-hamburger"
          >
            <Menu className="w-5 h-5" />
          </button>
          <UserMenu />
        </div>

        {/* Header */}
        <header className="text-center py-8 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 mb-4">
            <Sparkles className="w-4 h-4 text-[#d4af37]" />
            <span className="text-xs uppercase tracking-widest text-[#d4af37]">Numerologija</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl bg-gradient-to-r from-amber-400 via-purple-400 to-blue-400 bg-clip-text text-transparent mb-4">
            Skaičių žemėlapis
          </h1>
          <p className="text-foreground/70 text-base sm:text-lg leading-relaxed">
            Kiekvienas skaičius nuo 1 iki 9 – tai atskira archetipo šviesa ir potencialas. Susipažink su jų esme ir pasirink, kurio skaičiaus diagnostiką nori atlikti.
          </p>
        </header>

        {/* Numbers Grid */}
        <section className="max-w-6xl mx-auto mb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {NUMBERS_MAP.map(({ number, title, keyword, overview, question }) => (
              <Card
                key={number}
                className="card-mystical relative p-6 flex flex-col border-border/50 hover:shadow-glow transition-all duration-300 group"
                data-testid={`number-card-${number}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${NUMBER_COLORS[number]} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-2xl font-bold text-black/80">{number}</span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-lg text-foreground leading-tight">{title}</h3>
                    <p className="text-[11px] uppercase tracking-wider text-[#d4af37]/70 mt-1">{keyword}</p>
                  </div>
                </div>
                <div className="flex-1 mb-6">
                  <p className="text-sm text-foreground/70 leading-relaxed mb-3" data-testid={`number-overview-${number}`}>
                    {overview}
                  </p>
                  <p
                    className="text-sm text-foreground/70 leading-relaxed"
                    data-testid={`number-question-${number}`}
                  >
                    {question}
                  </p>
                </div>
                <Button
                  onClick={() => handleRunDiagnosis(number)}
                  className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold group/btn"
                  data-testid={`run-diagnosis-${number}`}
                >
                  Atlikti šio skaičiaus diagnostiką
                  <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </Card>
            ))}
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />

      {/* Global Navigation Menu */}
      <GlobalNavMenu isOpen={navOpen} onClose={() => setNavOpen(false)} />
    </div>
  );
}
